import type { ReactNode } from "react";
import { THEME_IDS, THEME_ROOT_ATTR, type ThemeId } from "@potok/ui";

export type AppView = "wizard" | "register" | "cabinet";

type AppShellProps = {
  /** Основное содержимое шага wizard / экрана. */
  children: ReactNode;
  /** Активная тема клиентского SPA (ФТ-8.3). */
  themeId?: ThemeId;
  /** Активный раздел шапки. */
  activeView?: AppView;
  onNavigate?: (view: AppView) => void;
  /** Есть ли сохранённая клиентская сессия (пункт «Кабинет»). */
  hasClientSession?: boolean;
  /** Полноэкранный auth-layout без шапки (страница входа). */
  minimal?: boolean;
};

/**
 * Базовый layout клиентского SPA.
 * Каркас First screen (Figma): тёмная шапка, марка, навигация разделов.
 */
export function AppShell({
  children,
  themeId = THEME_IDS.DEFAULT,
  activeView = "wizard",
  onNavigate,
  hasClientSession = false,
  minimal = false,
}: AppShellProps) {
  function go(view: AppView): void {
    const hash = view === "wizard" ? "" : view === "register" ? "#register" : "#client-access";
    if (hash) {
      window.location.hash = hash;
    } else if (window.location.hash) {
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    onNavigate?.(view);
  }

  return (
    <div className={minimal ? "shell shell--minimal" : "shell"} {...{ [THEME_ROOT_ATTR]: themeId }}>
      {minimal ? null : (
        <header className="shell__header">
          <div className="shell__header-inner">
            <div className="shell__brand-block">
              <span className="shell__mark" aria-hidden="true" />
              <div className="shell__brand-text">
                <p className="shell__brand">ПОТОК</p>
                <p className="shell__tagline">КМЗКО · konmash.ru</p>
              </div>
            </div>
            <nav className="shell__nav" aria-label="Разделы">
              <button
                type="button"
                className={
                  activeView === "wizard"
                    ? "shell__nav-link shell__nav-link--active"
                    : "shell__nav-link"
                }
                onClick={() => go("wizard")}
              >
                Подбор
              </button>
              <button
                type="button"
                className={
                  activeView === "register"
                    ? "shell__nav-link shell__nav-link--active"
                    : "shell__nav-link"
                }
                onClick={() => go("register")}
              >
                Регистрация
              </button>
              <button
                type="button"
                className={
                  activeView === "cabinet"
                    ? "shell__nav-link shell__nav-link--active"
                    : "shell__nav-link"
                }
                onClick={() => go("cabinet")}
              >
                {hasClientSession || activeView === "cabinet" ? "Кабинет" : "Войти"}
              </button>
            </nav>
          </div>
        </header>
      )}
      <main className={minimal ? "shell__main shell__main--minimal" : "shell__main"}>{children}</main>
    </div>
  );
}

export function viewFromHash(hash: string): AppView {
  const normalized = hash.replace(/^#/, "");
  if (normalized === "register") {
    return "register";
  }
  if (normalized === "client-access") {
    return "cabinet";
  }
  return "wizard";
}
